import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPatientHistory, clearPatientHistory } from "../../Redux/actions/doctorActions/doctorActions.js";
import Loading from "../Loading.jsx";
import Error from "../Error.jsx";
import "./DoctorModals.css";
import { convertTo12Hour } from "../../utils/timeUtils";

const ViewPatientHistoryModal = ({ patientId, onClose }) => {
  const dispatch = useDispatch();
  const { patientHistory, patientHistoryLoading, patientHistoryError } = useSelector(
    (state) => state.doctor
  );

  // ✅ State to track expanded appointment
  const [expandedAppointmentId, setExpandedAppointmentId] = useState(null);

  useEffect(() => {
    dispatch(getPatientHistory(patientId));
    return () => {
      dispatch(clearPatientHistory());
    };
  }, [dispatch, patientId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const formatDateTime = (dateString, time) => {
    const date = formatDate(dateString);
    const time12 = convertTo12Hour(time);
    return `${date} at ${time12}`;
  };

  // ✅ Toggle appointment expansion
  const toggleAppointment = (appointmentId) => {
    setExpandedAppointmentId(expandedAppointmentId === appointmentId ? null : appointmentId);
  };

  // ✅ Find medical record for an appointment
  const getMedicalRecordForAppointment = (appointmentId) => {
    return patientHistory?.medicalRecords?.find(
      record => record.appointmentId === appointmentId
    );
  };

  // ✅ NEW: Filter appointments to show only confirmed/completed ones with medical records
  const getFilteredAppointments = () => {
    if (!patientHistory?.appointments) return [];
    
    return patientHistory.appointments.filter((appt) => {
      // Check if appointment is confirmed or completed
      const isConfirmedOrCompleted = appt.status === "confirmed" || appt.status === "completed";
      
      // Check if appointment has a medical record
      const hasMedicalRecord = patientHistory.medicalRecords?.some(
        record => record.appointmentId === appt._id
      );
      
      // Return only appointments that meet both conditions
      return isConfirmedOrCompleted && hasMedicalRecord;
    });
  };

  // ✅ Get filtered appointments
  const filteredAppointments = getFilteredAppointments();

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-xl">
        <div className="modal-header-custom">
          <h4>
            <i className="bi bi-clock-history me-2"></i>
            Patient Medical History
          </h4>
          <button className="btn-close-custom" onClick={onClose}>
            <i className="bi bi-x-lg">Close</i>
          </button>
        </div>

        <div className="modal-body-custom">
          {patientHistoryLoading && <Loading />}
          {patientHistoryError && <Error message={patientHistoryError} />}

          {patientHistory && (
            <>
              {/* Patient Info Banner */}
              <div className="patient-info-banner mb-4">
                <div className="row">
                  <div className="col-md-3">
                    <strong>Name</strong>
                    <div className="text-dark fw-semibold">{patientHistory.patient.name}</div>
                  </div>
                  <div className="col-md-3">
                    <strong>MR Code : </strong>
                    <code>{patientHistory.patient.mrCode}</code>
                  </div>
                  <div className="col-md-3">
                    <strong>Email</strong>
                    <div className="text-dark">{patientHistory.patient.email}</div>
                  </div>
                </div>
              </div>

              {/* ✅ NEW: Recent Appointments with Expandable Details (Filtered) */}
              <div className="mb-0">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-bold">
                    <i className="bi bi-calendar3 me-2 text-info"></i>
                    Confirmed Appointments with Records
                  </h5>
                  <span className="badge bg-info">{filteredAppointments.length} appointments</span>
                </div>

                {filteredAppointments.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    No confirmed appointments with medical records found
                  </div>
                ) : (
                  <div className="appointments-expandable-list">
                    {filteredAppointments.map((appt) => {
                      const medicalRecord = getMedicalRecordForAppointment(appt._id);
                      const isExpanded = expandedAppointmentId === appt._id;

                      return (
                        <div key={appt._id} className="appointment-expandable-card mb-3">
                          {/* ✅ Clickable Appointment Header */}
                          <div 
                            className="appointment-header"
                            onClick={() => toggleAppointment(appt._id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-3">
                                  <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} text-primary`}></i>
                                  <div>
                                    <strong className="d-block">
                                      <i className="bi bi-calendar3 me-2"></i>
                                      {formatDateTime(appt.date, appt.time)}
                                    </strong>
                                    <small className="text-muted">
                                      <i className="bi bi-person-badge me-1"></i>
                                      Dr. {appt.doctor?.name} • {appt.department}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span
                                  className={`badge bg-${
                                    appt.status === "completed"
                                      ? "success"
                                      : "primary"
                                  }`}
                                >
                                  {appt.status}
                                </span>
                                {medicalRecord && (
                                  <span className="badge bg-info">
                                    <i className="bi bi-file-medical me-1"></i>
                                    Has Record
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* ✅ Expandable Medical Record Details */}
                          {isExpanded && medicalRecord && (
                            <div className="appointment-details-expanded">
                              {/* Minimize Button */}
                              <div className="text-end mb-2">
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAppointmentId(null);
                                  }}
                                >
                                  <i className="bi bi-dash-circle me-1"></i>
                                  Minimize
                                </button>
                              </div>

                              {/* Compact Two-Column Layout */}
                              <div className="row g-3">
                                {/* Left Column */}
                                <div className="col-md-6">
                                  {/* Chief Complaint */}
                                  <div className="detail-box mb-3">
                                    <div className="detail-label">
                                      <i className="bi bi-clipboard-pulse me-1 text-danger"></i>
                                      Chief Complaint
                                    </div>
                                    <div className="detail-value">{medicalRecord.chiefComplaint}</div>
                                  </div>

                                  {/* HOPC (Symptoms) */}
                                  {medicalRecord.symptoms && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-thermometer me-1 text-warning"></i>
                                        HOPC
                                      </div>
                                      <div className="detail-value">{medicalRecord.symptoms}</div>
                                    </div>
                                  )}

                                  {/* Diagnosis */}
                                  <div className="detail-box mb-3">
                                    <div className="detail-label">
                                      <i className="bi bi-clipboard-check me-1 text-success"></i>
                                      Diagnosis
                                    </div>
                                    <div className="detail-value fw-bold">{medicalRecord.diagnosis}</div>
                                  </div>

                                  {/* Physical Examination */}
                                  {medicalRecord.physicalExamination && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-stethoscope me-1 text-info"></i>
                                        Physical Examination
                                      </div>
                                      <div className="detail-value">{medicalRecord.physicalExamination}</div>
                                    </div>
                                  )}

                                  {/* Vitals */}
                                  {medicalRecord.vitals && Object.keys(medicalRecord.vitals).length > 0 && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-heart-pulse me-1 text-danger"></i>
                                        Vitals
                                      </div>
                                      <div className="d-flex flex-wrap gap-2">
                                        {medicalRecord.vitals.bp && (
                                          <span className="badge bg-light text-dark border">
                                            BP: {medicalRecord.vitals.bp}
                                          </span>
                                        )}
                                        {medicalRecord.vitals.weight && (
                                          <span className="badge bg-light text-dark border">
                                            Weight: {medicalRecord.vitals.weight}kg
                                          </span>
                                        )}
                                        {medicalRecord.vitals.spo2 && (
                                          <span className="badge bg-light text-dark border">
                                            SPO2: {medicalRecord.vitals.spo2}%
                                          </span>
                                        )}
                                        {medicalRecord.vitals.temperature && (
                                          <span className="badge bg-light text-dark border">
                                            Temp: {medicalRecord.vitals.temperature}°F
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Right Column */}
                                <div className="col-md-6">
                                  {/* Medicines */}
                                  {medicalRecord.medicines && medicalRecord.medicines.length > 0 && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-capsule me-1 text-success"></i>
                                        Medicines ({medicalRecord.medicines.length})
                                      </div>
                                      <div className="medicines-compact">
                                        {medicalRecord.medicines.slice(0, 3).map((med, idx) => (
                                          <div key={idx} className="medicine-item-compact mb-2">
                                            <div className="fw-semibold">{idx + 1}. {med.medicineName}</div>
                                            <small className="text-muted">
                                              {med.dosage} • {med.frequency} • {med.duration}
                                            </small>
                                          </div>
                                        ))}
                                        {medicalRecord.medicines.length > 3 && (
                                          <small className="text-muted">
                                            +{medicalRecord.medicines.length - 3} more medicines
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Lab Tests */}
                                  {medicalRecord.labTests && medicalRecord.labTests.length > 0 && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-clipboard2-pulse me-1 text-primary"></i>
                                        Lab Tests ({medicalRecord.labTests.length})
                                      </div>
                                      <div className="lab-tests-compact">
                                        {medicalRecord.labTests.map((test, idx) => (
                                          <div key={idx} className="lab-test-item-compact mb-1">
                                            <small>
                                              {idx + 1}. {test.testName}
                                              {test.urgent && (
                                                <span className="badge bg-danger ms-2">URGENT</span>
                                              )}
                                            </small>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Clinical Notes */}
                                  {medicalRecord.clinicalNotes && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-file-text me-1 text-secondary"></i>
                                        Clinical Notes
                                      </div>
                                      <div className="detail-value">{medicalRecord.clinicalNotes}</div>
                                    </div>
                                  )}

                                  {/* Referral */}
                                  {medicalRecord.referral?.isReferred && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-arrow-right-circle me-1 text-warning"></i>
                                        Referral
                                      </div>
                                      <div className="detail-value">
                                        <strong>Dr. {medicalRecord.referral.referredTo?.name}</strong>
                                        <br />
                                        <small className="text-muted">
                                          {medicalRecord.referral.referredTo?.specialization} • {medicalRecord.referral.reason}
                                        </small>
                                      </div>
                                    </div>
                                  )}

                                  {/* Follow-up */}
                                  {medicalRecord.followUpDate && (
                                    <div className="detail-box mb-3">
                                      <div className="detail-label">
                                        <i className="bi bi-calendar-check me-1 text-info"></i>
                                        Follow-up
                                      </div>
                                      <div className="detail-value">
                                        {formatDate(medicalRecord.followUpDate)}
                                        {medicalRecord.followUpNotes && (
                                          <><br /><small className="text-muted">{medicalRecord.followUpNotes}</small></>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer-custom">
          <button className="btn btn-secondary" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i>
            Close
          </button>
        </div>
      </div>

      {/* ✅ Add custom styles inline or in your CSS file */}
      <style jsx>{`
        .appointment-expandable-card {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .appointment-expandable-card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .appointment-header {
          padding: 16px;
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          transition: background 0.2s;
        }

        .appointment-header:hover {
          background: #e9ecef;
        }

        .appointment-details-expanded {
          padding: 20px;
          background: white;
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .detail-box {
          padding: 12px;
          background: #f8f9fa;
          border-left: 3px solid #667eea;
          border-radius: 4px;
        }

        .detail-label {
          font-weight: 600;
          font-size: 0.9rem;
          color: #495057;
          margin-bottom: 6px;
        }

        .detail-value {
          color: #212529;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .medicine-item-compact,
        .lab-test-item-compact {
          padding: 6px 0;
          border-bottom: 1px solid #e9ecef;
        }

        .medicine-item-compact:last-child,
        .lab-test-item-compact:last-child {
          border-bottom: none;
        }
      `}</style>
    </div>
  );
};

export default ViewPatientHistoryModal;